import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The shared account, as a context (SPEC-022 D-3).
 *
 * Adapted from habb.me's `useCloudAuth`: same providers (Google popup,
 * passwordless email link), same project, so a habb.me account is already an
 * account here (ADR-0003). Two deliberate departures:
 *
 *   - Errors are carried as message KEYS, never English strings — every word
 *     a visitor sees goes through `t()` in three catalogues, and a hook has no
 *     business choosing a language.
 *   - No `window.prompt` to recover a missing email-link address. A link
 *     opened on a device that never asked for one gets a stated error and a
 *     working form, not a browser dialog that cannot be translated.
 *
 * The default context value is a resolved guest: a tree rendered without the
 * provider — a unit test, mostly — is simply a signed-out tree.
 */

const IDLE_EMAIL_LINK_STATE = { status: 'idle', pendingEmail: '', errorKey: null };

const GUEST_AUTH = {
    user: null,
    authStatus: 'signed-out',
    isAuthResolved: true,
    isAuthenticated: false,
    signIn: () => Promise.resolve(),
    signOut: () => Promise.resolve(),
    sendEmailLink: () => Promise.resolve(),
    emailLinkState: IDLE_EMAIL_LINK_STATE,
};

const AuthContext = createContext(GUEST_AUTH);

function loadAuthContext() {
    return import('../../infrastructure/cloud/firebaseAuthClient').then(({ getAuthContext }) => getAuthContext());
}

function buildFallbackName(email) {
    if (typeof email !== 'string' || !email) {
        return null;
    }

    const [localPart] = email.split('@');

    return localPart || email;
}

function createUser(firebaseUser) {
    return {
        uid: firebaseUser.uid,
        name: firebaseUser.displayName || buildFallbackName(firebaseUser.email),
        email: firebaseUser.email,
    };
}

async function ensureUserSessionReady(firebaseUser) {
    if (firebaseUser && typeof firebaseUser.getIdToken === 'function') {
        await firebaseUser.getIdToken();
    }

    return firebaseUser;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [authStatus, setAuthStatus] = useState('loading');
    const [emailLinkState, setEmailLinkState] = useState(IDLE_EMAIL_LINK_STATE);

    useEffect(() => {
        let cancelled = false;
        let unsubscribe = () => {};

        loadAuthContext()
            .then((authContext) => {
                if (cancelled) {
                    return;
                }

                const {
                    auth,
                    onAuthStateChanged,
                    isSignInWithEmailLink,
                    signInWithEmailLink,
                    readPendingEmailLinkAddress,
                    clearPendingEmailLinkAddress,
                    clearEmailLinkFromLocation,
                } = authContext;

                if (typeof isSignInWithEmailLink === 'function'
                    && typeof window !== 'undefined'
                    && isSignInWithEmailLink(auth, window.location.href)) {
                    const pendingEmail = readPendingEmailLinkAddress();

                    if (pendingEmail) {
                        setEmailLinkState({ status: 'completing', pendingEmail, errorKey: null });

                        signInWithEmailLink(auth, pendingEmail, window.location.href)
                            .then(() => {
                                if (cancelled) {
                                    return;
                                }

                                clearPendingEmailLinkAddress();
                                clearEmailLinkFromLocation();
                                setEmailLinkState(IDLE_EMAIL_LINK_STATE);
                            })
                            .catch((error) => {
                                console.error(error);

                                if (!cancelled) {
                                    setEmailLinkState({
                                        status: 'error',
                                        pendingEmail,
                                        errorKey: 'settings.account.email.error.complete',
                                    });
                                }
                            });
                    } else {
                        // The link was opened on a device that never requested
                        // one, so there is no stored address to finish with.
                        setEmailLinkState({
                            status: 'error',
                            pendingEmail: '',
                            errorKey: 'settings.account.email.error.differentDevice',
                        });
                    }
                }

                unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
                    if (!firebaseUser) {
                        setAuthStatus('signed-out');
                        setUser(null);
                        return;
                    }

                    setAuthStatus('loading');

                    ensureUserSessionReady(firebaseUser)
                        .then(() => {
                            if (!cancelled) {
                                setAuthStatus('authenticated');
                                setUser(createUser(firebaseUser));
                            }
                        })
                        .catch((error) => {
                            console.error(error);

                            if (!cancelled) {
                                setAuthStatus('error');
                                setUser(null);
                            }
                        });
                });
            })
            .catch((error) => {
                // Auth failing to load must never take the shell down — the
                // whole product works signed out.
                console.error(error);

                if (!cancelled) {
                    setAuthStatus('error');
                    setUser(null);
                }
            });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    const value = useMemo(() => {
        function signIn() {
            setAuthStatus('loading');

            return loadAuthContext()
                .then(({ auth, provider, signInWithPopup }) => signInWithPopup(auth, provider))
                .then((result) => ensureUserSessionReady(result.user).then(() => {
                    setAuthStatus('authenticated');
                    setUser(createUser(result.user));
                }))
                .catch((error) => {
                    // A closed popup and a real failure end the same way: still
                    // signed out, and the button still there.
                    console.error(error);
                    setAuthStatus('signed-out');
                });
        }

        function sendEmailLink(email) {
            const normalizedEmail = typeof email === 'string' ? email.trim() : '';

            if (!normalizedEmail) {
                setEmailLinkState({ status: 'error', pendingEmail: '', errorKey: 'settings.account.email.error.empty' });
                return Promise.resolve();
            }

            setEmailLinkState({ status: 'sending', pendingEmail: normalizedEmail, errorKey: null });

            return import('../../infrastructure/cloud/firebaseAuthClient')
                .then((firebaseAuth) => firebaseAuth.getAuthContext().then((authContext) => authContext
                    .sendSignInLinkToEmail(authContext.auth, normalizedEmail, firebaseAuth.createEmailLinkActionCodeSettings())
                    .then(() => firebaseAuth.storePendingEmailLinkAddress(normalizedEmail))))
                .then(() => {
                    setEmailLinkState({ status: 'sent', pendingEmail: normalizedEmail, errorKey: null });
                })
                .catch((error) => {
                    console.error(error);
                    setEmailLinkState({
                        status: 'error',
                        pendingEmail: normalizedEmail,
                        errorKey: 'settings.account.email.error.send',
                    });
                });
        }

        function signOut() {
            setAuthStatus('loading');

            return loadAuthContext()
                .then(({ auth, signOut: signOutUser }) => signOutUser(auth))
                .then(() => {
                    setAuthStatus('signed-out');
                    setUser(null);
                })
                .catch((error) => {
                    console.error(error);
                    setAuthStatus('error');
                });
        }

        return {
            user,
            authStatus,
            isAuthResolved: authStatus !== 'loading',
            isAuthenticated: authStatus === 'authenticated' && Boolean(user),
            signIn,
            signOut,
            sendEmailLink,
            emailLinkState,
        };
    }, [user, authStatus, emailLinkState]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
